import { db } from "~/firebaseInstance";
import {
  deleteDoc,
  doc,
  getDocFromServer,
  setDoc,
  updateDoc,
  type DocumentReference,
} from "firebase/firestore";
import { debounce, isEqual } from "lodash";
import { useAuthStore } from "~/stores/auth";
import {
  clearCacheForCustomer,
  readCacheEntryRaw,
  writeCache,
  type CacheVersion,
} from "~/services/storage/idbCache";
import { readBypassCacheFromLocalStorage } from "~/composables/usePwaCacheFlag";
import { useApi } from "~/composables/useApi";
import {
  bucket,
  offlineBucketNames,
} from "~/services/storage/offlineBuckets";
import { plainify } from "~/utils/plainify";

// Firestore document holding the version map: { [resource]: hashString }.
// Path is customer_databases/{storeId}.
const VERSION_COLLECTION = "customer_databases";

export type VersionMap = Record<string, string>;

export type ResourceRegistration<T = unknown> = {
  resource: string;
  // Fetches fresh data from the network. Should be idempotent.
  refetch: () => Promise<T>;
  // Pushes fresh (or cached) data into the relevant Pinia store.
  applyToStore: (data: T) => void;
};

const registry = new Map<string, ResourceRegistration>();

export function registerResource<T>(reg: ResourceRegistration<T>): void {
  registry.set(reg.resource, reg as ResourceRegistration);
}

export function getRegistry(): ReadonlyMap<string, ResourceRegistration> {
  return registry;
}

function getCustomerId(): string | null {
  const auth = useAuthStore();
  return auth.storeId ? String(auth.storeId) : null;
}

function getVersionDocRef(): DocumentReference | null {
  const cid = getCustomerId();
  if (!cid) return null;
  return doc(db, VERSION_COLLECTION, cid);
}

/**
 * One-shot read of the Firestore version doc from the server. Returns null
 * when the SPA is offline or the read fails — caller should proceed with
 * whatever local IDB cache exists.
 */
export async function fetchRemoteVersions(): Promise<VersionMap | null> {
  const ref = getVersionDocRef();
  if (!ref) return null;
  try {
    const snap = await getDocFromServer(ref);
    if (!snap.exists()) return {};
    const data = snap.data() as Record<string, unknown>;
    const out: VersionMap = {};
    for (const [k, v] of Object.entries(data)) {
      if (typeof v === "string") out[k] = v;
      else if (typeof v === "number") out[k] = String(v);
    }
    return out;
  } catch (err) {
    console.warn("[versionSync] fetchRemoteVersions failed", err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Legacy FoodServer cache invalidation.
//
// When a shop edits its menu (item/catalog/subitem/tag/topping/comment) the
// server must purge the old FoodServer's Redis cache, otherwise legacy mobile
// apps keep showing a stale menu. The server exposes POST /legacy-cache/clear
// for this. All calls here are strictly best-effort: a failed cache clear must
// NEVER throw or block a menu edit / the Clear-cache button.
// ---------------------------------------------------------------------------

// Menu-related resources whose mutation should trigger a legacy cache clear.
// Names must match the `registerResource(...)` calls in pos.products.ts.
const MENU_RESOURCES = new Set<string>([
  "catalogs",
  "items",
  "subitems",
  "tags",
  "comments",
  "toppingGroups",
  "toppings",
]);

async function callLegacyCacheClearApi(immediate = false): Promise<void> {
  const cid = getCustomerId();
  if (!cid) return;
  try {
    await useApi().$api("/legacy-cache/clear", {
      method: "POST",
      body: { immediate },
    });
  } catch (err) {
    console.warn("[versionSync] legacy cache clear failed", err);
  }
}

// Single module-level debouncer: the SPA serves one store per session and the
// clear is per-customer (not per-resource), so a burst of menu edits across
// several resources collapses into one API call fired 1.5s after the last edit.
const legacyCacheClearDebounced = debounce(
  () => callLegacyCacheClearApi(false),
  1500,
  { leading: false, trailing: true }
);

function generateHash(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function reconcileOne(
  cid: string,
  reg: ResourceRegistration,
  remoteHash: CacheVersion
): Promise<void> {
  // Read the envelope unconditionally so the refetch-failure resilience path
  // below can still serve stale data when the API is dead — even if the user
  // has the bypass flag set.
  const envelope = await readCacheEntryRaw(cid, reg.resource);
  const bypassCache = readBypassCacheFromLocalStorage();

  // Hit: remote hash exists and matches local envelope. Skipped when the
  // bypass flag is on so callers always go to network.
  if (!bypassCache && remoteHash && envelope?.version === remoteHash) {
    reg.applyToStore(envelope.data);
    return;
  }

  // Miss: refetch from cloud.
  let fresh: unknown;
  try {
    fresh = await reg.refetch();
  } catch (err) {
    // Resilience: if the API server is down (frequent during deploys) but we
    // already hold a cached envelope, serve the stale data so the POS still
    // boots fully on local instead of crashing. Only a genuine cold boot
    // (no cache at all) propagates the error to the boot handler.
    if (envelope) {
      console.warn(
        `[versionSync:${reg.resource}] refetch failed — serving cached data`,
        err
      );
      reg.applyToStore(envelope.data);
      return;
    }
    throw err;
  }

  const safe = plainify(fresh);
  const hash = remoteHash ?? generateHash();
  await writeCache(cid, reg.resource, safe, hash);
  reg.applyToStore(safe);

  // Seed Firestore only when it didn't already track this resource.
  if (!remoteHash) await bumpResourceVersion(reg.resource, hash);
}

/**
 * Online bootstrap path. Fetches the Firestore version doc, then reconciles
 * each registered resource.
 *
 * `fetchRemoteVersions()` distinguishes two null-ish results:
 *  - `{}`   → version doc genuinely doesn't exist → reconcile (refetch + seed).
 *  - `null` → Firestore unreachable (degraded) → fall back to cache-only so a
 *             reachable-internet-but-dead-backend situation still boots local.
 */
export async function syncAllVersionsFromNetwork(): Promise<void> {
  const cid = getCustomerId();
  if (!cid) return;

  const remote = await fetchRemoteVersions();
  if (remote == null) {
    await serveAllVersionsFromCache();
    return;
  }

  await Promise.all(
    Array.from(registry.values()).map((reg) =>
      reconcileOne(cid, reg, remote[reg.resource] ?? null)
    )
  );
}

/**
 * Offline bootstrap path. Skips the Firestore probe entirely and serves
 * whatever IDB envelopes exist. Used by `OfflineBootStrategy`.
 */
export async function serveAllVersionsFromCache(): Promise<void> {
  const cid = getCustomerId();
  if (!cid) return;
  // Offline boot intentionally uses the raw read: if there's no internet,
  // honoring the bypass flag would just brick the POS. Cache is the only
  // option here.
  await Promise.all(
    Array.from(registry.values()).map(async (reg) => {
      const envelope = await readCacheEntryRaw(cid, reg.resource);
      if (envelope) reg.applyToStore(envelope.data);
    })
  );
}

/**
 * Force-refresh a single resource after a user mutation. Used by store
 * action helpers: POST/PUT → refreshResource('items') → new envelope + new
 * Firestore hash, both stamped with the same UUID.
 *
 * Requires online connectivity (mutations are only allowed online per the
 * project's offline policy). Throws if the registration is missing or the
 * Firestore write fails.
 */
export async function refreshResource(resource: string): Promise<void> {
  const cid = getCustomerId();
  if (!cid) throw new Error("[versionSync] no storeId");

  const reg = registry.get(resource);
  if (!reg) throw new Error(`[versionSync] resource "${resource}" not registered`);

  const fresh = await reg.refetch();
  const safe = plainify(fresh);
  const newHash = generateHash();

  await writeCache(cid, resource, safe, newHash);
  reg.applyToStore(safe);
  await bumpResourceVersion(resource, newHash);

  if (MENU_RESOURCES.has(resource)) legacyCacheClearDebounced();
}

/**
 * Background "stale-while-revalidate" correction for a resource whose backing
 * data can change OUT-OF-BAND — i.e. without anything bumping the Firestore
 * version hash. The canonical case is `customerInfo`: when a shop renews/extends
 * its subscription the backend updates `ExpireDate`, but no POS mutation runs, so
 * the version hash stays put and `reconcileOne` keeps serving the stale cached
 * date forever (the home page + `pos-expiry` middleware then show the old date).
 *
 * This refetches the resource, deep-compares it to the cached copy, and only when
 * they differ overwrites the local cache (PRESERVING the existing version hash, so
 * the next boot is still a clean cache hit) and pushes fresh data into the store.
 *
 * Designed to be fired-and-forgotten from the ONLINE boot path only. It must never
 * throw and never block: on any refetch failure (offline / backend down) it returns
 * silently and leaves the cache untouched, so offline-first boot is unaffected. It
 * does NOT bump Firestore — every terminal self-heals via this same pass on its own
 * boot.
 */
export async function revalidateResourceByData(resource: string): Promise<void> {
  const cid = getCustomerId();
  if (!cid) return;

  const reg = registry.get(resource);
  if (!reg) return;

  let fresh: unknown;
  try {
    fresh = await reg.refetch();
  } catch (err) {
    // Offline / backend down — leave cache untouched. This is what keeps the
    // background revalidation offline-safe.
    console.warn(`[versionSync:${resource}] revalidate refetch skipped`, err);
    return;
  }

  try {
    const safe = plainify(fresh);
    const envelope = await readCacheEntryRaw(cid, resource);
    if (isEqual(envelope?.data, safe)) return; // no change — the common case

    await writeCache(cid, resource, safe, envelope?.version ?? null);
    reg.applyToStore(safe);
  } catch (err) {
    console.warn(`[versionSync:${resource}] revalidate apply failed`, err);
  }
}

/**
 * Selective per-record update for a resource cached in column-array form
 * (`[{ data: [id, ...] }]`, id at `data[0]`). Instead of refetching the whole
 * collection, the caller supplies the freshly-fetched raw rows to upsert and/or
 * the ids to drop; this merges them into the cached envelope, pushes the result
 * to the store, and bumps the Firestore hash (same hash on both sides) so a
 * later reload is a cache hit and other terminals refetch in full.
 *
 * Falls back to a full `refreshResource` when no cache envelope exists yet
 * (cold) — there's no safe base to merge into.
 */
export async function mergeResourceRecords(
  resource: string,
  opts: { upsertRaw?: Array<{ data: unknown[] }>; removeIds?: Array<number | string> }
): Promise<void> {
  const cid = getCustomerId();
  if (!cid) throw new Error("[versionSync] no storeId");

  const reg = registry.get(resource);
  if (!reg) throw new Error(`[versionSync] resource "${resource}" not registered`);

  const envelope = await readCacheEntryRaw<Array<{ data: unknown[] }>>(cid, resource);
  if (!envelope || !Array.isArray(envelope.data)) {
    // No base to merge into — do a safe full refresh instead.
    await refreshResource(resource);
    return;
  }

  const upsertRaw = opts.upsertRaw ?? [];
  const removeIds = opts.removeIds ?? [];
  const idOf = (row: { data: unknown[] }): number => Number(row?.data?.[0]);
  const removeSet = new Set(removeIds.map((v) => Number(v)));
  const upsertMap = new Map<number, { data: unknown[] }>(
    upsertRaw.map((row) => [idOf(row), row])
  );

  let next = envelope.data
    .filter((row) => !removeSet.has(idOf(row)))
    .map((row) => upsertMap.get(idOf(row)) ?? row);
  // Append rows that didn't already exist (newly created).
  for (const row of upsertRaw) {
    if (!next.some((r) => idOf(r) === idOf(row))) next.push(row);
  }

  const safe = plainify(next);
  const newHash = generateHash();
  await writeCache(cid, resource, safe, newHash);
  reg.applyToStore(safe);
  await bumpResourceVersion(resource, newHash);

  if (MENU_RESOURCES.has(resource)) legacyCacheClearDebounced();
}

/**
 * Debounced wrapper around `refreshResource`. Multiple rapid mutations on the
 * same resource (e.g. saving five employees in a row) collapse into a single
 * refetch + Firestore bump fired `wait` ms after the last call.
 */
const refreshDebouncers = new Map<string, ReturnType<typeof debounce>>();
export function refreshResourceDebounced(resource: string, wait = 1500): void {
  let d = refreshDebouncers.get(resource);
  if (!d) {
    d = debounce(
      () => {
        refreshResource(resource).catch((err) =>
          console.warn(`[versionSync:${resource}] debounced refresh failed`, err)
        );
      },
      wait,
      { leading: false, trailing: true }
    );
    refreshDebouncers.set(resource, d);
  }
  d();
}

/**
 * Write a new hash for `resource` into the Firestore version doc. Caller is
 * expected to have already updated IDB with the same hash so both sides stay
 * aligned. Uses `setDoc(..., { merge: true })` so the doc is created on
 * first bump if it didn't exist.
 */
export async function bumpResourceVersion(
  resource: string,
  hash: string
): Promise<void> {
  const ref = getVersionDocRef();
  if (!ref) throw new Error("[versionSync] no storeId");
  try {
    await updateDoc(ref, { [resource]: hash });
  } catch (err) {
    // Most likely cause: doc doesn't exist yet. Fall back to setDoc-with-merge.
    try {
      await setDoc(ref, { [resource]: hash }, { merge: true });
    } catch (err2) {
      console.warn(`[versionSync:${resource}] bumpResourceVersion failed`, err2);
      throw err2;
    }
  }
}

/**
 * Test/debug helper. Clears the in-memory registry so a re-bootstrap on the
 * same page (e.g. tenant switch) doesn't carry stale registrations.
 */
export function clearRegistry(): void {
  registry.clear();
}

/**
 * Hard reset: wipe the IDB envelope store for this customer AND delete the
 * Firestore version doc. Used by the `/clear-cache` rescue page when local
 * cache and server have desynced and a fresh resync is the simplest fix.
 *
 * After this resolves, the caller should reload the page so bootstrap runs
 * cold: every resource falls into Case 5 (refetch + re-seed Firestore).
 *
 * Does NOT clear localStorage authSnapshot — the user stays logged in.
 */
export async function hardClearAllVersions(): Promise<void> {
  const cid = getCustomerId();
  if (!cid) throw new Error("[versionSync] no storeId");

  // 1. Delete Firestore version doc so every terminal entering the next
  //    bootstrap sees a missing version and refetches.
  const ref = getVersionDocRef();
  if (ref) {
    try {
      await deleteDoc(ref);
    } catch (err) {
      console.warn("[versionSync] hardClear: deleteDoc failed", err);
      // Continue with local clear even if Firestore delete fails.
    }
  }

  // 2. Wipe the per-customer IDB envelope store.
  await clearCacheForCustomer(cid);

  // 3. Drop in-memory registry so a same-page re-bootstrap doesn't carry
  //    stale callbacks (matters only if we ever skip the page reload).
  registry.clear();
}

// Layer B buckets whose data is transactional and must survive a cache
// reset. Used by `clearCacheExceptJobsAndReceipts` to derive the wipe list
// from `offlineBucketNames` instead of hardcoding bucket names.
const PRESERVED_BUCKETS = new Set<keyof typeof offlineBucketNames>([
  "jobs",
  "receipts",
]);

/**
 * Clear cache invoked from the top-bar "Clear cache" button. Wipes the
 * Firestore version doc, the version-tracked IDB cache (menus/settings/
 * employees/paymentType/etc), and every Layer B bucket EXCEPT those in
 * `PRESERVED_BUCKETS`. Caller reloads the page after this resolves so
 * bootstrap runs cold.
 */
export async function clearCacheExceptJobsAndReceipts(): Promise<void> {
  const cid = getCustomerId();
  if (!cid) throw new Error("[versionSync] no storeId");

  const versionRef = getVersionDocRef();
  const bucketsToClear = (
    Object.keys(offlineBucketNames) as Array<keyof typeof offlineBucketNames>
  )
    .filter((k) => !PRESERVED_BUCKETS.has(k))
    .map((k) => bucket(offlineBucketNames[k](cid)).clear());

  await Promise.all([
    versionRef
      ? deleteDoc(versionRef).catch((err) =>
          console.warn("[versionSync] clearCache: deleteDoc failed", err)
        )
      : Promise.resolve(),
    clearCacheForCustomer(cid),
    // Kill-switch: purge the legacy FoodServer cache immediately (bypass the
    // debounce), before the caller reloads the page. Best-effort.
    callLegacyCacheClearApi(true).catch(() => {}),
    ...bucketsToClear,
  ]);

  registry.clear();
}
